import { useState } from 'react';
import { Alert, Button, Card, Form, Input, Typography } from 'antd';
import { LockOutlined, UserOutlined } from '@ant-design/icons';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';

const { Title, Text } = Typography;

const loginSchema = z.object({
  email: z.string().email('Correo inválido'),
  password: z.string().min(1, 'La contraseña es obligatoria'),
});

type LoginValues = z.infer<typeof loginSchema>;

interface LocationState {
  from?: { pathname: string };
}

export function LoginPage(): JSX.Element {
  const login = useAuthStore((s) => s.login);
  const navigate = useNavigate();
  const location = useLocation();
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const { control, handleSubmit, formState } = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = async (values: LoginValues): Promise<void> => {
    setServerError(null);
    setSubmitting(true);
    try {
      await login(values.email, values.password);
      const state = location.state as LocationState | undefined;
      const target = state?.from?.pathname ?? '/';
      navigate(target, { replace: true });
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'No fue posible iniciar sesión';
      setServerError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background:
          'linear-gradient(135deg, rgba(22,119,255,0.08) 0%, rgba(22,119,255,0.18) 100%)',
        padding: 16,
      }}
    >
      <Card style={{ width: '100%', maxWidth: 380 }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <Title level={2} style={{ margin: 0 }}>
            never2busy
          </Title>
          <Text type="secondary">CRM interno</Text>
        </div>

        {serverError && (
          <Alert
            type="error"
            message={serverError}
            showIcon
            style={{ marginBottom: 16 }}
            closable
            onClose={() => setServerError(null)}
          />
        )}

        <Form layout="vertical" onFinish={handleSubmit(onSubmit)} disabled={submitting}>
          <Controller
            name="email"
            control={control}
            render={({ field, fieldState }) => (
              <Form.Item
                label="Correo"
                validateStatus={fieldState.error ? 'error' : ''}
                help={fieldState.error?.message}
              >
                <Input
                  {...field}
                  prefix={<UserOutlined />}
                  size="large"
                  autoComplete="email"
                  placeholder="tu@empresa.com"
                />
              </Form.Item>
            )}
          />
          <Controller
            name="password"
            control={control}
            render={({ field, fieldState }) => (
              <Form.Item
                label="Contraseña"
                validateStatus={fieldState.error ? 'error' : ''}
                help={fieldState.error?.message}
              >
                <Input.Password
                  {...field}
                  prefix={<LockOutlined />}
                  size="large"
                  autoComplete="current-password"
                  placeholder="••••••••"
                />
              </Form.Item>
            )}
          />
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            block
            loading={submitting || formState.isSubmitting}
          >
            Iniciar sesión
          </Button>
        </Form>
      </Card>
    </div>
  );
}
